import { useQueryClient } from '@tanstack/react-query';
import { toCsv, type ImportResult } from '@zenarty/shared';
import { CheckCircle2, Download, FileUp, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api } from '../api';
import { Badge, Button, Callout, ErrorMessage } from './kit';
import { Dialog } from './overlays';
import { useToast } from './toast';

/** Two-step CSV import: validate (dry run, row-level report), then commit. */
export function ImportDialog({
  title,
  description,
  endpoint,
  scope,
  scopeReady,
  picker,
  templateHeaders,
  templateExample,
  templateName,
  invalidate,
  onClose,
}: {
  title: string;
  description: ReactNode;
  endpoint: string;
  /** Body fields identifying the target (clientId or siteId). */
  scope: Record<string, string>;
  scopeReady: boolean;
  /** Target picker shown above the file (client or site). */
  picker?: ReactNode;
  templateHeaders: string[];
  templateExample: (string | number)[];
  templateName: string;
  invalidate: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async (dryRun: boolean) => {
    if (!csv) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<ImportResult>(endpoint, {
        method: 'POST',
        body: { csv, dryRun, ...scope },
      });
      setResult(r);
      if (!dryRun && !r.errors.length) {
        await qc.invalidateQueries({ queryKey: [invalidate] });
        await qc.invalidateQueries({ queryKey: ['dashboard'] });
        toast.ok(
          `Imported: ${r.created.toLocaleString()} created, ${r.updated.toLocaleString()} updated`,
        );
        onClose();
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const template = () => {
    const blob = new Blob([toCsv(templateHeaders, [templateExample])], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = templateName;
    a.click();
  };

  const ready = result && result.dryRun && result.errors.length === 0 && result.valid > 0;
  return (
    <Dialog
      title={title}
      description={description}
      onClose={onClose}
      wide
      footer={
        <>
          <Button
            variant="ghost"
            icon={Download}
            onClick={template}
            style={{ marginRight: 'auto' }}
          >
            Download template
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {ready ? (
            <Button variant="primary" icon={Upload} loading={busy} onClick={() => void run(false)}>
              Import {(result.created + result.updated).toLocaleString()} rows
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={CheckCircle2}
              loading={busy}
              disabled={!csv || !scopeReady}
              onClick={() => void run(true)}
            >
              Validate file
            </Button>
          )}
        </>
      }
    >
      {picker}
      <label
        className="card"
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 28,
          borderStyle: 'dashed',
          cursor: 'pointer',
          textAlign: 'center',
          gap: 6,
        }}
      >
        <FileUp size={26} className="muted" />
        <strong>{fileName || 'Choose a CSV file'}</strong>
        <span className="small muted">
          {csv
            ? `${(csv.length / 1024).toFixed(0)} KB · ${Math.max(0, csv.trim().split(/\r?\n/).length - 1).toLocaleString()} rows`
            : 'UTF-8 CSV with a header row, up to 50,000 rows'}
        </span>
        <input
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setFileName(f.name);
            setResult(null);
            void f.text().then(setCsv);
          }}
        />
      </label>
      <ErrorMessage error={error} />
      {result ? (
        result.errors.length ? (
          <div className="stack-sm">
            <Callout
              tone="danger"
              title={`${(result.total - result.valid).toLocaleString()} of ${result.total.toLocaleString()} rows have problems`}
            >
              Nothing was imported. Fix these rows and validate again.
            </Callout>
            <div className="card table-card" style={{ maxHeight: 280, overflow: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Column</th>
                    <th>Problem</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i}>
                      <td className="num">{e.row}</td>
                      <td>{e.column ? <span className="code-pill">{e.column}</span> : '—'}</td>
                      <td>{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.truncated ? (
              <span className="small muted">Showing the first 500 problems.</span>
            ) : null}
          </div>
        ) : (
          <Callout tone="ok" title="File is valid">
            <div className="row" style={{ marginTop: 4 }}>
              <Badge tone="ok">{result.created.toLocaleString()} new</Badge>
              <Badge tone="info">{result.updated.toLocaleString()} updates</Badge>
              <span className="muted small">{result.total.toLocaleString()} rows checked</span>
            </div>
          </Callout>
        )
      ) : null}
    </Dialog>
  );
}
